'use client';

import { useState } from 'react';
import { updateSettings } from "@/lib/actions/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Globe, Mail, Phone, MapPin, Linkedin, Instagram, Server, ShieldCheck, Map as MapIcon, Settings2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "../shared/RichTextEditor";
import { ChangePasswordForm } from "./ChangePasswordForm";

interface SettingsFormProps {
  initialData: Record<string, string>;
}

export function SettingsForm({ initialData }: SettingsFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Contact Info
    contact_email: initialData.contact_email || 'eizel@simisi.id',
    contact_phone: initialData.contact_phone || '+62 819-0568-1347',
    office_address: initialData.office_address || 'Jakarta, Indonesia',
    website_url: initialData.website_url || 'www.simisi.id',
    google_maps_url: initialData.google_maps_url || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3966.521!2d106.8023!3d-6.2245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f40145b45d7d%3A0x5765b9f68891f3e7!2sTreasury%20Tower!5e0!3m2!1sen!2sid!4v1700000000000!5m2!1sen!2sid',
    
    // Social
    linkedin_url: initialData.linkedin_url || 'https://linkedin.com/company/simisi',
    instagram_url: initialData.instagram_url || 'https://instagram.com/simisi.co',
    
    // SMTP Settings
    smtp_host: initialData.smtp_host || '',
    smtp_port: initialData.smtp_port || '465',
    smtp_user: initialData.smtp_user || '',
    smtp_pass: initialData.smtp_pass || '',
    smtp_from: initialData.smtp_from || '',
    contact_recipient_email: initialData.contact_recipient_email || 'eizel@simisi.id',

    // Features
    enable_career: initialData.enable_career !== 'false', // Default to true if not set
    enable_lms: initialData.enable_lms === 'true', // Default to false if not set
    lms_url: initialData.lms_url || 'https://lms.simisi.id',

    // Home Page Content
    home_hero_headline: initialData.home_hero_headline || '',
    home_hero_description: initialData.home_hero_description || '',
    home_service_intro: initialData.home_service_intro || '',
    home_cta_description: initialData.home_cta_description || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Convert boolean to string for storage
      const dataToSave = {
        ...formData,
        enable_career: String(formData.enable_career),
        enable_lms: String(formData.enable_lms),
      };
      const result = await updateSettings(dataToSave);

      if (result.success) {
        toast.success("Settings updated successfully");
      } else {
        toast.error("Failed to update settings");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      <div>
        <h1 className="text-3xl font-bold text-navy font-heading">Global Settings</h1>
        <p className="text-gray mt-1 font-sans">Manage static information, social links, and SMTP configuration.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Feature Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              <CardTitle className="font-heading">Feature Settings</CardTitle>
            </div>
            <CardDescription className="font-sans">Enable or disable specific website modules.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-light rounded-xl bg-light/10">
              <div className="space-y-0.5">
                <Label htmlFor="enable_career" className="text-base font-bold text-navy font-heading">Enable Career Section</Label>
                <p className="text-sm text-gray font-sans italic">Show or hide 'Career' and 'Join the Team' links in the header.</p>
              </div>
              <Switch
                id="enable_career"
                checked={formData.enable_career}
                onCheckedChange={(checked) => handleToggleChange('enable_career', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-light rounded-xl bg-light/10">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="enable_lms" className="text-base font-bold text-navy font-heading">Enable LMS Link</Label>
                  <div className="bg-primary/10 text-primary text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Moodle</div>
                </div>
                <p className="text-sm text-gray font-sans italic">Show an 'LMS' button in the website header.</p>
              </div>
              <Switch
                id="enable_lms"
                checked={formData.enable_lms}
                onCheckedChange={(checked) => handleToggleChange('enable_lms', checked)}
              />
            </div>

            {formData.enable_lms && (
              <div className="p-4 border border-primary/20 rounded-xl bg-primary/5 space-y-4 animate-in fade-in slide-in-from-top-2">
                <div className="space-y-2">
                  <Label htmlFor="lms_url" className="text-navy font-bold">LMS Platform URL</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input 
                      id="lms_url" 
                      name="lms_url" 
                      value={formData.lms_url} 
                      onChange={handleChange} 
                      className="pl-10 border-primary/20 focus:border-primary bg-white" 
                      placeholder="https://lms.simisi.id"
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-primary" />
              <CardTitle className="font-heading">Contact Information</CardTitle>
            </div>
            <CardDescription className="font-sans">Primary contact details for clients and inquiries.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Public Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="contact_email" name="contact_email" value={formData.contact_email} onChange={handleChange} className="pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Phone / WhatsApp</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="contact_phone" name="contact_phone" value={formData.contact_phone} onChange={handleChange} className="pl-10" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="office_address">Office Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input id="office_address" name="office_address" value={formData.office_address} onChange={handleChange} className="pl-10" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="google_maps_url">Google Maps Embed URL</Label>
              <div className="relative">
                <MapIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input id="google_maps_url" name="google_maps_url" value={formData.google_maps_url} onChange={handleChange} className="pl-10" placeholder="https://www.google.com/maps/embed?pb=..." />
              </div>
              <p className="text-[11px] text-gray-400">Paste the 'src' attribute from the Google Maps embed iframe code.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL Display</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input id="website_url" name="website_url" value={formData.website_url} onChange={handleChange} className="pl-10" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMTP Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              <CardTitle className="font-heading">SMTP Settings</CardTitle>
            </div>
            <CardDescription className="font-sans">Configure SMTP to receive contact form emails.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="smtp_host">SMTP Host</Label>
                <Input id="smtp_host" name="smtp_host" value={formData.smtp_host} onChange={handleChange} placeholder="smtp.gmail.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">Port</Label>
                <Input id="smtp_port" name="smtp_port" value={formData.smtp_port} onChange={handleChange} placeholder="465" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_user">SMTP User</Label>
                <Input id="smtp_user" name="smtp_user" value={formData.smtp_user} onChange={handleChange} placeholder="user@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_pass">SMTP Password</Label>
                <Input id="smtp_pass" name="smtp_pass" type="password" value={formData.smtp_pass} onChange={handleChange} placeholder="••••••••" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="smtp_from">"From" Email Name</Label>
                <Input id="smtp_from" name="smtp_from" value={formData.smtp_from} onChange={handleChange} placeholder="Simisi Portal" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_recipient_email">Recipient Email</Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="contact_recipient_email" name="contact_recipient_email" value={formData.contact_recipient_email} onChange={handleChange} className="pl-10" placeholder="admin@simisi.id" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Home Page Content */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <CardTitle className="font-heading">Home Page Content</CardTitle>
            </div>
            <CardDescription className="font-sans">Manage primary content on the home page.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="home_hero_headline">Hero Headline</Label>
              <RichTextEditor 
                value={formData.home_hero_headline} 
                onChange={(val) => handleManualChange('home_hero_headline', val)} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="home_hero_description">Hero Description</Label>
              <RichTextEditor 
                value={formData.home_hero_description} 
                onChange={(val) => handleManualChange('home_hero_description', val)} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="home_service_intro">Service Section Intro</Label>
              <RichTextEditor 
                value={formData.home_service_intro} 
                onChange={(val) => handleManualChange('home_service_intro', val)} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="home_cta_description">CTA Section Description</Label>
              <RichTextEditor 
                value={formData.home_cta_description} 
                onChange={(val) => handleManualChange('home_cta_description', val)} 
              />
            </div>
          </CardContent>
        </Card>

        {/* Social Media */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Linkedin className="h-5 w-5 text-blue-600" />
              <CardTitle>Social Media</CardTitle>
            </div>
            <CardDescription>Links to Simisi&Co. social profiles.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="linkedin_url">LinkedIn Profile URL</Label>
              <Input id="linkedin_url" name="linkedin_url" value={formData.linkedin_url} onChange={handleChange} placeholder="https://linkedin.com/company/simisi" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instagram_url">Instagram Profile URL</Label>
              <Input id="instagram_url" name="instagram_url" value={formData.instagram_url} onChange={handleChange} placeholder="https://instagram.com/simisi.co" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" className="bg-navy hover:bg-primary px-8 transition-all" disabled={isLoading}>
            {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save All Settings</>}
          </Button>
        </div>
      </form>

      {/* Security Settings */}
      <ChangePasswordForm />
    </div>
  );
}
